import Splide from '@splidejs/splide';

export default class StaticNanobar extends HTMLElement {
  countdown: ReturnType<typeof setInterval> | null = null;

  connectedCallback() {
    this.init();
  }

  /**
   * Initializes all nanobar functionality: slider, countdown timer, and close button.
   */
  init() {
    const slideCount = parseInt(this.dataset.slideCount || '1', 10);
    const slideDuration = parseInt(this.dataset.slideDuration || '7000', 10);

    this.initSlides(slideCount, slideDuration);
    this.setMaxWidth();
    this.initCountdownIfPresent();
    this.initClose();

    this.classList.add('active');

    // Refresh overlay event listeners for any modal content
    document.dispatchEvent(new Event('theme:overlay:refresh'));
  }

  /**
   * Mounts the Splide carousel on the slide container.
   * @param {number} slideCount - The number of slides.
   * @param {number} slideDuration - Autoplay interval in milliseconds.
   */
  initSlides(slideCount: number, slideDuration: number) {
    const splideEl = this.querySelector<HTMLElement>('.nano--slide-container');
    if (!splideEl) return;

    const options = {
      type: 'loop',
      autoplay: slideCount > 1,
      pauseOnHover: true,
      interval: slideDuration,
      pagination: false,
      drag: slideCount > 1,
    };

    const splide = new Splide(splideEl, options);

    setTimeout(() => splide.mount(), 0);
  }

  /**
   * Checks for a countdown date on the element and starts the timer if present.
   */
  initCountdownIfPresent() {
    const countdownEl = this.querySelector<HTMLElement>(
      '[data-countdown-date]'
    );
    const date = countdownEl?.dataset.countdownDate;
    if (!date || !countdownEl) return;

    this.updateCountdown(date, countdownEl);
    this.countdown = setInterval(
      () => this.updateCountdown(date, countdownEl),
      1000
    );
  }

  /**
   * Updates the countdown display every second. Clears the interval when the deadline passes.
   * @param {string} date - The countdown end date string (YYYY-MM-DD).
   * @param {HTMLElement} countdownEl - The element to render the countdown into.
   */
  updateCountdown(date: string, countdownEl: HTMLElement) {
    const deadline = new Date(date.replace(/-/g, '/')).getTime();
    const distance = deadline - new Date().getTime();

    if (distance > 0) {
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      const daysCopy = days === 1 ? ' day, ' : ' days, ';
      const hoursCopy = hours === 1 ? ' hour, ' : ' hours, ';

      countdownEl.innerHTML = `&nbsp;${days > 0 ? days + daysCopy : ''}${
        hours > 0 ? hours + hoursCopy : ''
      }${minutes > 0 ? minutes + ' min,' : ''} ${seconds} sec`;
      this.querySelector('.clock-wrapper')?.classList.remove('empty');
    } else {
      clearInterval(this.countdown!);
      this.setAttribute('data-timer', 'false');
      this.querySelector('.clock-wrapper')?.classList.add('empty');
      this.querySelector('.clock--module')?.remove();
    }
  }

  /**
   * Measures the rendered width of each slide's text content and constrains
   * .nano--type so the countdown timer has room beside the slider.
   */
  setMaxWidth() {
    const nanoType = this.querySelector<HTMLElement>('.nano--type');
    if (!nanoType) return;

    let maxWidth = 0;

    this.querySelectorAll<HTMLElement>('.slide--text').forEach((slideText) => {
      const width = slideText.getBoundingClientRect().width;
      if (width > maxWidth) maxWidth = width;
    });

    if (maxWidth > 0) {
      nanoType.style.maxWidth = `${maxWidth + 100}px`;
    }
  }

  /**
   * Attaches the close button click handler to hide the nanobar.
   */
  initClose() {
    this.querySelector('.nano--close')?.addEventListener('click', () => {
      this.remove();
    });
  }
}

customElements.define('static-nano-bar', StaticNanobar);
